/* Academy: business lessons and a knowledge-check quiz, sourced from the
 * engine's teaching content. Results persist to the class record (Phase 3 #2)
 * so the instructor's Grading tab reflects real quiz completion. */
import { useState } from 'react';
import { Card, Button, Badge } from '../../components/ui';
import { LESSONS, QUIZ, type Named } from '../../lib/constants';
import { useSubmitQuiz } from '../../hooks/queries';
import type { ScreenProps } from './types';

export function Academy({ detail }: ScreenProps) {
  const lessons = LESSONS as Named[];
  const quiz = QUIZ as unknown as Array<{ q: string; opts: string[]; a: number; why: string }>;
  const [openLesson, setOpenLesson] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [submitted, setSubmitted] = useState(false);
  const submitQuiz = useSubmitQuiz(detail.game.classId);

  const score = quiz.reduce((n, q, i) => n + (answers[i] === q.a ? 1 : 0), 0);

  const check = () => {
    setSubmitted(true);
    // Persist the attempt — the server grades the answers against the engine's
    // own key, so the score shown here is only a local echo. Instructors
    // playing their own class's demo game aren't enrolled — a 403 here is
    // expected and harmless, so ignore errors.
    if (detail.game.classId) {
      submitQuiz.mutate({
        lessonId: 'knowledge-check',
        answers: Object.fromEntries(Object.entries(answers).map(([k, v]) => [String(k), v])),
      });
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-mut">Lessons</h3>
        {lessons.map((l) => {
          const open = openLesson === l.id;
          return (
            <Card key={l.id}>
              <button className="flex w-full items-center justify-between text-left" onClick={() => setOpenLesson(open ? null : l.id)}>
                <span className="font-semibold">{(l.icon as string)} {l.title as string}</span>
                <Badge tone="mut">{l.tag as string}</Badge>
              </button>
              {open && (
                <div className="prose-academy mt-3 space-y-2 text-sm text-mut" dangerouslySetInnerHTML={{ __html: l.body as string }} />
              )}
            </Card>
          );
        })}
      </section>

      <section className="space-y-3">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-mut">Knowledge check</h3>
        <Card className="space-y-4">
          {quiz.map((q, i) => {
            const picked = answers[i];
            return (
              <div key={i}>
                <div className="text-sm font-medium">{i + 1}. {q.q}</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {q.opts.map((opt, oi) => {
                    const isPicked = picked === oi;
                    const correct = submitted && oi === q.a;
                    const wrong = submitted && isPicked && oi !== q.a;
                    return (
                      <button key={oi} disabled={submitted}
                        onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))}
                        className={`chip border ${correct ? 'border-good/60 bg-good/10 text-good' : wrong ? 'border-bad/60 bg-bad/10 text-bad' : isPicked ? 'border-acc/70 bg-acc/10 text-acc' : 'border-line text-mut hover:bg-[color:var(--hover-fill)]'}`}>
                        {opt}
                      </button>
                    );
                  })}
                </div>
                {submitted && <div className="mt-1 text-xs text-dim">{q.why}</div>}
              </div>
            );
          })}
          {!submitted ? (
            <Button onClick={check} disabled={Object.keys(answers).length < quiz.length}>Check answers</Button>
          ) : (
            <div className="flex items-center gap-3">
              <Badge tone={score === quiz.length ? 'good' : score >= quiz.length / 2 ? 'warn' : 'bad'}>Score {score} / {quiz.length}</Badge>
              {detail.game.classId && (
                <span className="text-xs text-dim">
                  {submitQuiz.isPending ? 'Saving…' : submitQuiz.isSuccess ? 'Recorded for your class grade' : submitQuiz.isError ? 'Not recorded (not enrolled)' : ''}
                </span>
              )}
              <button className="text-sm text-acc hover:underline" onClick={() => { setSubmitted(false); setAnswers({}); submitQuiz.reset(); }}>Retry</button>
            </div>
          )}
        </Card>
      </section>
    </div>
  );
}
